import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { ApiError } from '@/api/client';

/**
 * Routes a failed request onto the form.
 *
 * `AuthApiController.ToActionResult` turns an `AccountOperationResult` into a
 * `ValidationProblemDetails` keyed by field name, and `ApiError.fieldErrors`
 * hands that back unchanged. Two things fall out of how the server builds it:
 *
 *  - `AccountService` sets `AccountError.Field` to the empty string for nearly
 *    every failure it reports itself — a wrong current password, a spent reset
 *    token, an Identity password-policy rejection. Those arrive under a `""` key
 *    and belong in the summary alert, which is exactly what
 *    `asp-validation-summary` did with them.
 *  - Model-binding failures are keyed by property name instead, so those can be
 *    attached to the input they came from.
 *
 * Matching is case-insensitive because the two paths disagree on casing: the
 * JSON serializer camel-cases binding errors while `AddModelError` is given the
 * name verbatim.
 */
export function useServerValidation<T extends FieldValues>(
  setError: UseFormSetError<T>,
  fields: readonly Path<T>[],
) {
  const { t } = useTranslation();
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const clearFormErrors = useCallback(() => setFormErrors([]), []);

  /**
   * For outcomes the API reports in a 200 body rather than as an error — the
   * login endpoint's `succeeded: false` is the only one, by design.
   */
  const showFormError = useCallback((message: string) => setFormErrors([message]), []);

  const applyServerErrors = useCallback(
    (error: unknown) => {
      const fallback = t('auth:Auth.UnexpectedError', 'Something went wrong. Please try again.');

      if (!(error instanceof ApiError)) {
        setFormErrors([fallback]);
        return;
      }

      const entries = Object.entries(error.fieldErrors);
      if (entries.length === 0) {
        setFormErrors([error.message || fallback]);
        return;
      }

      const unattached: string[] = [];

      for (const [key, messages] of entries) {
        const field = fields.find((name) => name.toLowerCase() === key.toLowerCase());
        if (field) {
          setError(field, { type: 'server', message: messages.join(' ') });
        } else {
          unattached.push(...messages);
        }
      }

      setFormErrors(unattached);
    },
    [fields, setError, t],
  );

  return { formErrors, clearFormErrors, showFormError, applyServerErrors };
}
